import {type FC, useCallback} from 'react';
import {styled} from 'styled-components';

import {getNzbCheckHealth} from '@repo/shared/lib/nzb_check_stats';

import {ButtonAsLink, UnthemedNavLink} from '@repo/shared-web/components/core/button';

import {formatDate} from '@repo/website-frontend/lib/date';
import {queueCheck, useCheckingQueue} from '@repo/website-frontend/stores/checking_queue_store';
import {useNzbsu} from '@repo/website-frontend/stores/nzbsu_store';

interface NzbCheckProps {
  guid: string;
}

export const NzbCheck: FC<NzbCheckProps> = props => {
  const {guid} = props;
  const item = useNzbsu(guid);
  const checkStatus = useCheckingQueue(guid);
  const handleCheckClick = useCallback(() => {
    queueCheck(guid);
  }, [guid]);

  const check = item?.lastCheck;
  const passed = getNzbCheckHealth(check) === 'healthy' && check?.checked === 10;
  const checkColor = !check ? '#8b9099' : passed ? '#4caf50' : '#ef5350';
  const checkTitle = check
    ? `${check.outcome === 'failed' ? 'Check failed' : `${check.success}/10 samples passed`}; ${formatDate(new Date(check.ts))}`
    : 'Never checked';
  const blockedByLock =
    checkStatus?.stage === 'failed' &&
    checkStatus.err instanceof Error &&
    checkStatus.err.message.includes('Another NZB check is running');
  const checkButtonTitle =
    checkStatus?.stage === 'queued'
      ? 'This check is queued and will start automatically.'
      : checkStatus?.stage === 'failed'
        ? blockedByLock
          ? 'Another NZB check is running. Turn off the Newsgroup toggle, wait for the current run to finish, then click retry.'
          : 'The check could not be completed. Click to try again.'
        : 'Check availability, audio, and subtitles';
  const checkButtonLabel =
    checkStatus?.stage === 'inprogress'
      ? 'checking…'
      : checkStatus?.stage === 'queued'
        ? 'queued'
        : checkStatus?.stage === 'failed'
          ? blockedByLock
            ? 'busy'
            : 'retry'
          : check
            ? 're-check'
            : 'check';

  return (
    <Controls>
      <HealthLink
        to={`/debug/nzb-health?guid=${encodeURIComponent(guid)}`}
        title={`${checkTitle}. View check details`}
        aria-label={`${checkTitle}. View check details`}
      >
        <CheckCircle $color={checkColor} aria-hidden="true" />
      </HealthLink>
      <CheckButton
        onClick={handleCheckClick}
        disabled={checkStatus?.stage === 'queued'}
        loading={checkStatus?.stage === 'inprogress'}
        title={checkButtonTitle}
        aria-label={checkButtonTitle}
      >
        {checkButtonLabel}
      </CheckButton>
    </Controls>
  );
};
NzbCheck.displayName = 'NzbCheck';

const Controls = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
`;

const HealthLink = styled(UnthemedNavLink)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  &:hover {
    background-color: #ffffff15;
  }
`;

const CheckButton = styled(ButtonAsLink)`
  height: 24px;
  padding: 0 6px;
  border-radius: 4px;
  font-size: 12px;
  &:hover {
    background-color: #ffffff15;
  }
`;

const CheckCircle = styled.div<{$color: string}>`
  background-color: ${p => p.$color};
  width: 12px;
  height: 12px;
  border-radius: 50%;
`;
